import { useEffect, useState } from "react";

import { getSupabaseClient, getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl } from "@/lib/supabase/client";
import type { SupabaseSessionUser } from "@/lib/supabase/auth";
import { getSessionUser, refreshSessionViaServer } from "@/lib/supabase/auth";

export function useSupabaseSession() {
  const allowDevAuth = Boolean(import.meta.env.DEV);

  const readDevUser = () => {
    if (!allowDevAuth) {
      try {
        globalThis.localStorage?.removeItem("dev_auth_user");
      } catch {
        // ignore
      }
      return null;
    }

    try {
      const raw = globalThis.localStorage?.getItem("dev_auth_user") ?? "";
      if (!raw) return null;
      const parsed = JSON.parse(raw) as any;
      if (!parsed?.id) return null;
      return parsed as SupabaseSessionUser;
    } catch {
      return null;
    }
  };

  const readStoredSupabaseUser = () => {
    try {
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      if (!storageKey) return null;

      const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
      if (!raw) return null;

      const parsed = JSON.parse(raw) as any;
      const storedUser = parsed?.user;
      const storedId = String(storedUser?.id ?? "").trim();
      if (!storedId) return null;

      return {
        id: storedId,
        email: typeof storedUser?.email === "string" ? storedUser.email : null,
        metadata: (storedUser?.user_metadata ?? {}) as Record<string, unknown>,
      } as SupabaseSessionUser;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState<SupabaseSessionUser | null>(() => readDevUser() ?? readStoredSupabaseUser());
  const [loading, setLoading] = useState(() => ((readDevUser() ?? readStoredSupabaseUser()) ? false : true));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let sessionVersion = 0;

    // Safety: never keep the UI stuck on "Yükleniyor...".
    // If session retrieval hangs for any reason, we still let the UI continue and use dev auth if available.
    const failOpen = setTimeout(() => {
      if (!mounted) return;
      setUser((prev) => prev ?? readDevUser() ?? readStoredSupabaseUser());
      setLoading(false);
    }, 1500);

    const bootstrap = async () => {
      const bootstrapVersion = sessionVersion;

      try {
        const u = await getSessionUser();
        if (u) {
          if (mounted && bootstrapVersion === sessionVersion) setUser(u ?? readDevUser() ?? readStoredSupabaseUser());
          return;
        }

        // If we have a refresh token in storage but no readable user yet, silently refresh via server.
        try {
          const { directUrl } = getSupabaseConfig();
          const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
          if (storageKey) {
            const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
            const parsed = raw ? (JSON.parse(raw) as any) : null;
            const refresh_token = typeof parsed?.refresh_token === "string" ? parsed.refresh_token : "";
            const expires_at = typeof parsed?.expires_at === "number" ? parsed.expires_at : Number(parsed?.expires_at ?? 0) || 0;
            const now = Math.floor(Date.now() / 1000);

            if (refresh_token && (!expires_at || expires_at < now + 120)) {
              const refreshed = await refreshSessionViaServer(refresh_token);

              // Persist refreshed session back to storage.
              const expiresIn = typeof (refreshed as any)?.expires_in === "number" ? (refreshed as any).expires_in : 3600;
              const sessionToStore = {
                access_token: (refreshed as any).access_token,
                refresh_token: (refreshed as any).refresh_token,
                token_type: (refreshed as any).token_type ?? "bearer",
                expires_in: expiresIn,
                expires_at: now + expiresIn,
                user: (refreshed as any).user ?? null,
              };

              globalThis.localStorage?.setItem(storageKey, JSON.stringify(sessionToStore));

              const refreshedUserId = String((refreshed as any)?.user?.id ?? "").trim();
              if (refreshedUserId) {
                globalThis.localStorage?.setItem("karma_last_auth_user_id", refreshedUserId);
                globalThis.sessionStorage?.setItem("karma_store_session_user_id", refreshedUserId);
              }

              globalThis.dispatchEvent?.(new Event("supabase-session-changed"));
            }
          }
        } catch {
          // ignore
        }

        const u2 = await getSessionUser();
        if (mounted && bootstrapVersion === sessionVersion) setUser(u2 ?? readDevUser() ?? readStoredSupabaseUser());
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Supabase oturumu alınamadı";
        if (mounted) setError(msg);
      } finally {
        clearTimeout(failOpen);
        if (mounted) setLoading(false);
      }
    };

    void bootstrap();

    const onDevAuthChanged = () => {
      if (!mounted) return;
      sessionVersion += 1;
      setUser(readDevUser() ?? readStoredSupabaseUser());
    };

    const onSupabaseSessionChanged = () => {
      if (!mounted) return;
      sessionVersion += 1;
      const eventVersion = sessionVersion;

      void (async () => {
        try {
          const u = await getSessionUser();
          if (mounted && eventVersion === sessionVersion) setUser(u ?? readDevUser() ?? readStoredSupabaseUser());
        } catch {
          if (mounted && eventVersion === sessionVersion) setUser(readDevUser() ?? readStoredSupabaseUser());
        } finally {
          if (mounted) setLoading(false);
        }
      })();
    };

    globalThis.addEventListener?.("dev-auth-changed", onDevAuthChanged);
    globalThis.addEventListener?.("supabase-session-changed", onSupabaseSessionChanged);

    try {
      const supabase = getSupabaseClient();
      const { data } = supabase.auth.onAuthStateChange(async () => {
        sessionVersion += 1;
        const authVersion = sessionVersion;

        try {
          const u = await getSessionUser();
          if (mounted && authVersion === sessionVersion) setUser(u ?? readDevUser() ?? readStoredSupabaseUser());
        } catch {
          if (mounted && authVersion === sessionVersion) setUser(readDevUser() ?? readStoredSupabaseUser());
        }
      });

      return () => {
        mounted = false;
        clearTimeout(failOpen);
        globalThis.removeEventListener?.("dev-auth-changed", onDevAuthChanged);
        globalThis.removeEventListener?.("supabase-session-changed", onSupabaseSessionChanged);
        data.subscription.unsubscribe();
      };
    } catch {
      return () => {
        mounted = false;
        clearTimeout(failOpen);
        globalThis.removeEventListener?.("dev-auth-changed", onDevAuthChanged);
        globalThis.removeEventListener?.("supabase-session-changed", onSupabaseSessionChanged);
      };
    }
  }, []);

  return { user, loading, error };
}
